import React, { useState, useEffect } from 'react';
import { mockQuiz, mockQuestionStats } from '../../data/carouselMockData';

const LiveAnalyticsSlide: React.FC = () => {
  const [animatedValues, setAnimatedValues] = useState<number[]>([0, 0, 0, 0]);
  const questionIndex = 3; // Show question 4 (Venus hottest planet)
  const question = mockQuiz.questions[questionIndex];
  const stats = mockQuestionStats[questionIndex];

  useEffect(() => {
    // Animate the bar chart values
    const targetValues = Object.values(stats.answer_distribution);
    let frame = 0;
    const totalFrames = 30;

    const interval = setInterval(() => {
      frame++;
      if (frame <= totalFrames) {
        setAnimatedValues(targetValues.map(val =>
          Math.floor((val * frame) / totalFrames)
        ));
      } else {
        clearInterval(interval);
      }
    }, 30);

    return () => clearInterval(interval);
  }, [stats.answer_distribution]);

  const maxCount = Math.max(...Object.values(stats.answer_distribution));
  const responseRate = (stats.total_answered / 18) * 100;

  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Live Question Analytics
          </h2>
          <p className="text-xl text-gray-600 mb-6">
            See exactly how your class responds in real-time
          </p>
        </div>

        {/* Question Display */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  Question 4 of 10
                </span>
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                  Live
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                {question.question}
              </h3>
            </div>
          </div>

          {/* Answer Distribution */}
          <div className="space-y-3 mb-6">
            {question.answers.map((answer, idx) => {
              const count = animatedValues[idx] || 0;
              const percentage = maxCount > 0 ? (count / stats.total_answered) * 100 : 0;
              const isCorrect = idx === question.correct_index;

              return (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg font-bold text-gray-700">
                    {String.fromCharCode(65 + idx)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        {answer}
                        {isCorrect && <span className="ml-2 text-green-600">✓</span>}
                      </span>
                      <span className="text-sm font-semibold text-gray-600">
                        {count} student{count !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-8 overflow-hidden">
                      <div
                        className={`h-full flex items-center justify-end pr-3 text-white text-sm font-semibold transition-all duration-300 ${
                          isCorrect ? 'bg-green-500' : 'bg-orange-400'
                        }`}
                        style={{ width: `${percentage}%` }}
                      >
                        {percentage > 15 && `${percentage.toFixed(0)}%`}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Real-time Stats */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-500">
                {responseRate.toFixed(0)}%
              </div>
              <div className="text-sm text-gray-600 mt-1">Response Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-500">
                {stats.correct_percentage.toFixed(0)}%
              </div>
              <div className="text-sm text-gray-600 mt-1">Got it Right</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-500">
                {stats.average_time_seconds.toFixed(1)}s
              </div>
              <div className="text-sm text-gray-600 mt-1">Avg Response Time</div>
            </div>
          </div>
        </div>

        {/* Difficulty Indicator */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border-2 border-yellow-200 p-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <h4 className="text-lg font-bold text-gray-900">
              {stats.correct_percentage >= 80 ? 'Easy Question' : stats.correct_percentage >= 50 ? 'Medium Difficulty' : 'Challenging Question'}
            </h4>
          </div>
          <p className="text-sm text-gray-700">
            {stats.correct_percentage < 60 && 'This topic may need review in your next class'}
            {stats.correct_percentage >= 60 && stats.correct_percentage < 80 && 'Most students understand this concept well'}
            {stats.correct_percentage >= 80 && 'Students have mastered this topic!'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LiveAnalyticsSlide;
